import { useState, useEffect, useCallback, useMemo } from 'react';
import type { CostCentre } from '@/types/timesheet';

const STORAGE_KEY = 'cost-centre-selector-state';
const MAX_RECENT_COST_CENTRES = 10;

export interface CostCentreSelectorState {
  favourites: string[];
  recentlyUsed: Array<{ id: string; timestamp: number }>;
}

/**
 * Hook to manage cost centre selector state including favourites and recently used cost centres
 */
export function useCostCentreSelector(costCentres: CostCentre[]) {
  const [state, setState] = useState<CostCentreSelectorState>(() => {
    // Load from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          favourites: parsed.favourites || [],
          recentlyUsed: parsed.recentlyUsed || [],
        };
      }
    } catch (error) {
      console.error('Failed to load cost centre selector state:', error);
    }
    return { favourites: [], recentlyUsed: [] };
  });

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save cost centre selector state:', error);
    }
  }, [state]);

  // Toggle favourite status
  const toggleFavourite = useCallback((costCentreId: string) => {
    setState((prev) => {
      const isFavourite = prev.favourites.includes(costCentreId);
      return {
        ...prev,
        favourites: isFavourite
          ? prev.favourites.filter((id) => id !== costCentreId)
          : [...prev.favourites, costCentreId],
      };
    });
  }, []);

  // Record cost centre access
  const recordCostCentreAccess = useCallback((costCentreId: string) => {
    setState((prev) => {
      // Remove existing entry if present
      const filtered = prev.recentlyUsed.filter((item) => item.id !== costCentreId);
      
      // Add to front with current timestamp
      const updated = [
        { id: costCentreId, timestamp: Date.now() },
        ...filtered,
      ].slice(0, MAX_RECENT_COST_CENTRES);

      return {
        ...prev,
        recentlyUsed: updated,
      };
    });
  }, []);

  // Get enriched cost centres with favourite and recently used metadata
  const enrichedCostCentres = useMemo(() => {
    return costCentres.map((costCentre) => ({
      ...costCentre,
      isFavourite: state.favourites.includes(costCentre.id),
      lastAccessedDate: state.recentlyUsed.find((item) => item.id === costCentre.id)
        ? new Date(state.recentlyUsed.find((item) => item.id === costCentre.id)!.timestamp)
        : undefined,
    }));
  }, [costCentres, state.favourites, state.recentlyUsed]);

  // Get favourite cost centres
  const favouriteCostCentres = useMemo(() => {
    return enrichedCostCentres
      .filter((cc) => cc.isFavourite)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [enrichedCostCentres]);

  // Get recently used cost centres (excluding favourites)
  const recentCostCentres = useMemo(() => {
    const recentIds = state.recentlyUsed.map((item) => item.id);
    return enrichedCostCentres
      .filter((cc) => recentIds.includes(cc.id) && !cc.isFavourite)
      .sort((a, b) => {
        const aTime = a.lastAccessedDate?.getTime() || 0;
        const bTime = b.lastAccessedDate?.getTime() || 0;
        return bTime - aTime;
      })
      .slice(0, MAX_RECENT_COST_CENTRES);
  }, [enrichedCostCentres, state.recentlyUsed]);

  return {
    enrichedCostCentres,
    favouriteCostCentres,
    recentCostCentres,
    toggleFavourite,
    recordCostCentreAccess,
    isFavourite: (costCentreId: string) => state.favourites.includes(costCentreId),
  };
}
